import React, { useState } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Stack,
  IconButton,
  Tooltip,
  InputAdornment,
} from "@mui/material";
import {
  Phone as PhoneIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Add as AddIcon,
} from "@mui/icons-material";
import { HttpsCallable, httpsCallable } from "firebase/functions";

import { firefuncs } from "config/firebase";
import { formatPhoneNumber } from "utils/helperFunctions";
import { RootState } from "store";

// Extended User type with phoneNumber field
interface UserWithPhone {
  uid: string;
  email: string | null;
  displayName?: string | null;
  photoURL?: string | null;
  emailVerified: boolean;
  phoneNumber?: string;
}

interface FormState {
  phoneNumber: string;
  verificationCode: string;
  isEditing: boolean;
  isVerifying: boolean;
  error: string | null;
  success: string | null;
}

interface GenerateCodeParams {
  phoneNumber: string;
  userId: string;
}

interface GenerateCodeResponse {
  success: boolean;
  message?: string;
}

interface ValidateCodeParams {
  code: string;
}

interface ValidateCodeResponse {
  msg: string;
}

const UpdatePhoneNumber: React.FC = () => {
  const user = useSelector(
    (state: RootState) => state.user.user,
  ) as UserWithPhone | null;

  const [loading, setLoading] = useState<boolean>(false);
  
  const [formState, setFormState] = useState<FormState>({
    phoneNumber: "",
    verificationCode: "",
    isEditing: false,
    isVerifying: false,
    error: null,
    success: null,
  });

  // Type-safe Firebase functions
  const generateCode: HttpsCallable<GenerateCodeParams, GenerateCodeResponse> =
    httpsCallable(firefuncs, "generateCode");

  const validateCode: HttpsCallable<ValidateCodeParams, ValidateCodeResponse> =
    httpsCallable(firefuncs, "validateCode");

  const sendVerificationCode = async () => {
    if (!user) {
      setFormState(prev => ({ ...prev, error: "User not found" }));
      return;
    }

    const cleanNumber = formState.phoneNumber.replace(/\D/g, "");
    
    if (cleanNumber.length !== 10) {
      setFormState(prev => ({ ...prev, error: "Please enter a valid 10-digit phone number" }));
      return;
    }

    const e164Number = `+1${cleanNumber}`;
    setLoading(true);
    setFormState(prev => ({ ...prev, error: null }));

    try {
      await generateCode({
        phoneNumber: e164Number,
        userId: user.uid,
      });

      setFormState(prev => ({
        ...prev,
        isVerifying: true,
        success: `Verification code sent to ${formatPhoneNumber(cleanNumber)}`,
      }));
    } catch (err) {
      console.error("Error sending verification code", err);
      const errorMessage = err instanceof Error ? err.message : "Failed to send verification code";
      setFormState(prev => ({ ...prev, error: errorMessage }));
    } finally {
      setLoading(false);
    }
  };

  const handlePhoneInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const cleanValue = value.replace(/\D/g, "");
    const formattedValue = cleanValue ? formatPhoneNumber(cleanValue) : "";
    
    setFormState(prev => ({ 
      ...prev, 
      phoneNumber: formattedValue,
      error: null 
    }));
  };

  const handleValidateCode = async () => {
    if (!formState.verificationCode) {
      setFormState(prev => ({ ...prev, error: "Please enter the verification code" }));
      return;
    }

    setLoading(true);
    setFormState(prev => ({ ...prev, error: null }));

    try {
      const result = await validateCode({ code: formState.verificationCode });
      setFormState(prev => ({
        ...prev,
        success: result.data.msg,
        isEditing: false,
        isVerifying: false,
        phoneNumber: "",
        verificationCode: "",
      }));
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Verification failed";
      setFormState(prev => ({ ...prev, error: errorMessage }));
    } finally {
      setLoading(false);
    }
  };

  const startEditing = () => {
    setFormState(prev => ({
      ...prev,
      isEditing: true,
      error: null,
      success: null,
    }));
  };

  const cancelEditing = () => {
    setFormState({
      phoneNumber: "",
      verificationCode: "",
      isEditing: false,
      isVerifying: false,
      error: null,
      success: null,
    });
  };

  if (!user) return null;

  return (
    <Box sx={{ width: "100%" }}>
      {/* Current Phone Number Display */}
      {!formState.isEditing && (
        <Stack direction="row" spacing={2} alignItems="center" justifyContent="center">
          <Typography variant="body1">
            {user.phoneNumber ? (
              `+1 ${formatPhoneNumber(user.phoneNumber)}`
            ) : (
              <Chip
                label="None on file"
                variant="outlined"
                size="small"
                color="warning"
              />
            )}
          </Typography>
          <Tooltip title={user.phoneNumber ? "Update Phone Number" : "Add Phone Number"}>
            <IconButton 
              onClick={startEditing}
              color="primary"
              size="small"
            >
              {user.phoneNumber ? <EditIcon /> : <AddIcon />}
            </IconButton>
          </Tooltip>
        </Stack>
      )}

      {/* Phone Number Input Form */}
      {formState.isEditing && !formState.isVerifying && (
        <Box sx={{ mt: 2, display: "flex", flexDirection: "column", alignItems: "center" }}>
          <TextField
            label="Phone Number"
            value={formState.phoneNumber}
            onChange={handlePhoneInputChange}
            placeholder="(555) 123-4567"
            type="tel"
            autoComplete="tel"
            error={!!formState.error}
            helperText={formState.error}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Typography variant="body2" color="text.secondary">
                    +1
                  </Typography>
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2, maxWidth: 300 }}
          />
          
          <Stack direction="row" spacing={1} justifyContent="center">
            <Button
              onClick={sendVerificationCode}
              variant="contained"
              startIcon={loading ? <CircularProgress size={16} /> : <SaveIcon />}
              disabled={loading || !formState.phoneNumber}
            >
              {loading ? "Sending..." : "Send Code"}
            </Button>
            <Button
              onClick={cancelEditing}
              variant="outlined"
              startIcon={<CancelIcon />}
              disabled={loading}
            >
              Cancel
            </Button>
          </Stack>
        </Box>
      )}

      {/* Verification Code Input */}
      {formState.isVerifying && (
        <Box sx={{ mt: 2, display: "flex", flexDirection: "column", alignItems: "center" }}>
          <TextField
            label="Verification Code"
            value={formState.verificationCode}
            onChange={(e) => setFormState(prev => ({ 
              ...prev, 
              verificationCode: e.target.value,
              error: null 
            }))}
            placeholder="Enter 6-digit code"
            type="text"
            autoComplete="off"
            error={!!formState.error}
            helperText={formState.error}
            sx={{ mb: 2, maxWidth: 300 }}
          />
          
          <Stack direction="row" spacing={1} justifyContent="center">
            <Button
              onClick={handleValidateCode}
              variant="contained"
              startIcon={loading ? <CircularProgress size={16} /> : <SaveIcon />}
              disabled={loading || !formState.verificationCode}
            >
              {loading ? "Verifying..." : "Verify"}
            </Button>
            <Button
              onClick={cancelEditing}
              variant="outlined"
              startIcon={<CancelIcon />}
              disabled={loading}
            >
              Cancel
            </Button>
          </Stack>
        </Box>
      )}

      {/* Success/Error Messages */}
      {formState.success && (
        <Alert severity="success" sx={{ mt: 2 }} onClose={() => setFormState(prev => ({ ...prev, success: null }))}>
          {formState.success}
        </Alert>
      )}
      
      {formState.error && !formState.isEditing && !formState.isVerifying && (
        <Alert severity="error" sx={{ mt: 2 }} onClose={() => setFormState(prev => ({ ...prev, error: null }))}>
          {formState.error}
        </Alert>
      )}
    </Box>
  );
};

export default UpdatePhoneNumber;
